"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { HyperliquidConnectionStatus, HyperliquidReferrerEligibilityResult } from "@cede/types";
import { API_ENDPOINT, HYPERLIQUID_APPROVAL_DOMAIN, postHyperliquidExchange, QueryKeys } from "@cede/utils";
import {
  ApprovalMessage,
  HYPERLIQUID_CHAIN_MAINNET,
  HYPERLIQUID_APPROVAL_TYPES,
  APPROVE_AGENT_PRIMARY,
  HYPERLIQUID_DEFAULT_CHAIN_ID_HEX,
} from "@cede/utils";
import { useHyperliquidConnectionConfig } from "./HyperliquidConnectionConfigContext";
import { useHyperliquidSign } from "./useHyperliquidSign";
import { WALLET_STATUS } from "@/constants/wallet";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useApi } from "@/hooks/core/useApi";
import { useAuthenticatedWalletConnection } from "@/hooks/core/useAuthenticatedWalletConnection";
import { useSignTypedDataWithConnection } from "@/hooks/core/useSignTypedDataWithConnection";
import { isWalletActionError, WALLET_ACTION_ERROR_CODE } from "@/hooks/core/useWalletActionGuard";
import { useHubUser } from "@/hooks/user/useHubUser";
import * as Sentry from "@sentry/nextjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useAccount } from "wagmi";

const POLLING_INTERVAL_MS = 5000;
const MAX_POLLING_ATTEMPTS = 24; // 2 minutes max (24 * 5s)

/**
 * Session status for Hyperliquid connection flow
 * Used by trading widgets to determine which UI state to show
 */
export type HyperliquidSessionStatus =
  | "checking-wallet"
  | "checking-eligibility"
  | "not-eligible"
  | "needs-wallet"
  | "needs-approval"
  | "checking-activation"
  | "needs-preparation"
  | "checking-socials"
  | "needs-social-accounts"
  | "ready";

const DEFAULT_AGENT_DATA = {
  accountId: 0,
  agentAddress: "",
  agentName: "",
  status: HyperliquidConnectionStatus.WAITING_APPROVAL,
};

export type HyperliquidConnectionMode = "default" | "airdrop";

interface UseHyperliquidConnectionOptions {
  /** Flow-specific behavior for eligibility handling and caching */
  mode?: HyperliquidConnectionMode;
  /** Optional custom agent name (max 16 characters) */
  agentName?: string;
}

/**
 * Hook for managing Hyperliquid connection flow
 * Handles agent wallet approval, activation checking, deposits, and transfers
 *
 * Flow optimization:
 * 1. First, try to get/create agent data (backend checks eligibility internally)
 * 2. If user already has an account, skip eligibility check entirely
 * 3. Only check eligibility separately if the add endpoint fails with eligibility error
 */
export function useHyperliquidConnection(options?: UseHyperliquidConnectionOptions) {
  const { agentName, mode = "default" } = options ?? {};
  // Campaign-page subtree sets `disableEligibilityCheck` via HyperliquidConnectionConfigProvider;
  // outside that provider it falls back to `false` (referee gate stays enforced).
  const disableEligibilityCheck = useHyperliquidConnectionConfig()?.disableEligibilityCheck ?? false;
  const skipEligibility = mode === "airdrop" || disableEligibilityCheck;
  const agentNameRef = useRef(agentName);
  agentNameRef.current = agentName;
  const { auth, hubUser, walletStatus } = useHubUser();
  const apiHelper = useApi();
  const { address: wagmiAddress } = useAccount();
  const { authenticatedWalletAddress } = useAuthenticatedWalletConnection();
  const { signTransfer } = useHyperliquidSign();
  const { authenticated } = useAuthenticationProvider();
  const { signTypedDataAsync } = useSignTypedDataWithConnection();
  const queryClient = useQueryClient();
  const connectedAddress =
    walletStatus === WALLET_STATUS.CONNECTED ? (authenticatedWalletAddress ?? wagmiAddress) : undefined;
  const isWalletConnected = Boolean(connectedAddress);

  // Polling state
  const [isPollingStatus, setIsPollingStatus] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef(0);
  const agentDataQueryKey = useMemo(
    () => [QueryKeys.HYPERLIQUID_AGENT_DATA, hubUser?.id, mode, disableEligibilityCheck] as const,
    [hubUser?.id, mode, disableEligibilityCheck],
  );

  // Get Hyperliquid agent wallet data or add a new one
  // This endpoint handles eligibility check internally and returns existing account if one exists
  // We fetch this FIRST to avoid unnecessary eligibility checks for existing users
  const {
    data: agentData,
    isFetching: isLoadingAgent,
    error: agentError,
    refetch: refetchAgentData,
  } = useQuery({
    queryKey: agentDataQueryKey,
    queryFn: async () => {
      const queryParams: Record<string, string> = {};
      if (skipEligibility) queryParams.skipEligibility = "true";
      if (agentNameRef.current) queryParams.agentName = agentNameRef.current;

      const userAgentData = await apiHelper.postAuth(auth, API_ENDPOINT.EXCHANGE_ACCOUNT_ADD_HYPERLIQUID, {
        ...(Object.keys(queryParams).length > 0 ? { queryParams } : {}),
      });
      return userAgentData;
    },
    enabled: Boolean(authenticated && hubUser?.id),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: false, // Don't retry on eligibility errors
  });

  // Check if the agent error is an eligibility error (user not eligible)
  const isNotEligibleError = agentError instanceof Error && agentError.message.includes("isn't eligible");

  // Determine if we have a connected account (accountId > 0 means we have a real account)
  const hasAccount = agentData && agentData.accountId > 0;

  // Check if we're waiting for agent data (query enabled but no response yet)
  // This covers the brief moment between query becoming enabled and fetch starting
  const queryEnabled = Boolean(authenticated && hubUser?.id);
  const isPendingAgentData = queryEnabled && !agentData && !agentError;
  // Also consider "checking" when authenticated but waiting for hubUser data
  const isWaitingForUserData = authenticated && !hubUser?.id;

  // Check eligibility ONLY if:
  // 1. User doesn't have an account yet (agent data query failed or returned nothing), AND
  // 2. We got an eligibility error from the add endpoint
  // For existing users, we skip this entirely since they're already connected
  const shouldCheckEligibility = !hasAccount && isNotEligibleError;

  const {
    data: eligibilityData,
    isLoading: isCheckingEligibility,
    error: eligibilityError,
  } = useQuery<HyperliquidReferrerEligibilityResult>({
    queryKey: [QueryKeys.HYPERLIQUID_ELIGIBILITY, hubUser?.id],
    queryFn: async () => {
      const result = await apiHelper.getWithAuth(auth, API_ENDPOINT.EXCHANGE_ACCOUNT_HYPERLIQUID_ELIGIBILITY);
      return result;
    },
    enabled: Boolean(authenticated && hubUser?.id && shouldCheckEligibility),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // For existing users, consider them eligible (they already passed eligibility when they connected)
  const effectiveEligibility: HyperliquidReferrerEligibilityResult | undefined = hasAccount
    ? { eligible: true }
    : eligibilityData;

  // Computed isCheckingEligibility for return
  // Include isWaitingForUserData to cover when authenticated but hubUser not loaded yet
  const computedIsCheckingEligibility =
    isLoadingAgent || isCheckingEligibility || isPendingAgentData || isWaitingForUserData;

  // Approve agent wallet
  const {
    mutateAsync: approveAgent,
    isPending: isApproving,
    error: approvalError,
    reset: resetApprovalError,
  } = useMutation({
    mutationFn: async () => {
      // Read the latest agent data from the query cache to avoid stale closures
      // (e.g. after refetchAgentData updates the name but before React re-renders).
      const latestAgentData = queryClient.getQueryData<typeof agentData>(agentDataQueryKey) ?? agentData;
      if (!latestAgentData) throw new Error("Agent data not found");

      const nonce = Date.now();

      // EIP-712 message (agentName defaults to empty string)
      const message: ApprovalMessage = {
        hyperliquidChain: HYPERLIQUID_CHAIN_MAINNET,
        agentAddress: latestAgentData.agentAddress.toLowerCase() as `0x${string}`,
        agentName: latestAgentData.agentName ?? "",
        nonce: BigInt(nonce),
      };

      // Make the user sign the message
      const signature = await signTypedDataAsync({
        domain: HYPERLIQUID_APPROVAL_DOMAIN,
        types: HYPERLIQUID_APPROVAL_TYPES,
        primaryType: APPROVE_AGENT_PRIMARY,
        message,
      });

      // Send the signature to the backend to create the agent on Hyperliquid
      const response = await apiHelper.postAuth(auth, API_ENDPOINT.EXCHANGE_ACCOUNT_APPROVE_HYPERLIQUID, {
        bodyParams: { signature, nonce },
      });

      return response;
    },
    onSuccess: (newStatus) => {
      // Update the cached agent data with the new status
      if (agentData && hubUser?.id) {
        queryClient.setQueryData(agentDataQueryKey, {
          ...agentData,
          status: newStatus.status,
        });
      }

      // Refetch hub user so exchangeAccounts list includes the new HL account
      queryClient.invalidateQueries({ queryKey: [QueryKeys.HUB_USER_ACCOUNTS] });
      // Re-validate agent wallet so isAgentValid flips to true
      queryClient.invalidateQueries({ queryKey: ["agent-wallet-validation"] });

      enqueueSnackbar("Agent approved successfully", { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to approve agent: ${error.message}`, { variant: "error" });
    },
  });

  // Check Hyperliquid account status - used to check if the account is activated after deposit
  const { mutateAsync: updateHyperliquidAgentStatus, isPending: isUpdatingAgentStatus } = useMutation({
    mutationFn: async () => {
      const status = await apiHelper.postAuth(auth, API_ENDPOINT.EXCHANGE_ACCOUNT_HYPERLIQUID_STATUS);
      return status;
    },
    onSuccess: (newStatus) => {
      // Update the cached agent data with the new status
      if (agentData && hubUser?.id) {
        queryClient.setQueryData(agentDataQueryKey, {
          ...agentData,
          status: newStatus.status,
        });
      }
      if (newStatus.status !== HyperliquidConnectionStatus.WAITING_DEPOSIT) {
        enqueueSnackbar("Hyperliquid account activated successfully", { variant: "success" });
      } else {
        enqueueSnackbar("Account is not activated yet. Hyperliquid might take a few minutes to activate it.", {
          variant: "warning",
          autoHideDuration: 10000,
        });
      }
    },
    onError: () => {
      enqueueSnackbar("Failed to check Hyperliquid account status. Please try again later.", { variant: "error" });
    },
  });

  // Stop polling
  const stopPollingStatus = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPollingStatus(false);
    pollingAttemptsRef.current = 0;
  }, []);

  // Poll for status updates after deposit (silent, no snackbars on each attempt)
  const startPollingStatus = useCallback(async () => {
    // Don't start if already polling
    if (pollingIntervalRef.current) return;

    setIsPollingStatus(true);
    pollingAttemptsRef.current = 0;

    const checkStatus = async () => {
      try {
        pollingAttemptsRef.current += 1;
        const status = await apiHelper.postAuth(auth, API_ENDPOINT.EXCHANGE_ACCOUNT_HYPERLIQUID_STATUS);

        // Update cached data silently
        if (agentData && hubUser?.id) {
          queryClient.setQueryData(agentDataQueryKey, {
            ...agentData,
            status: status.status,
          });
        }

        // Success - account activated
        if (status.status !== HyperliquidConnectionStatus.WAITING_DEPOSIT) {
          stopPollingStatus();
          enqueueSnackbar("Hyperliquid account activated successfully", { variant: "success" });
          return status;
        }

        // Max attempts reached
        if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
          stopPollingStatus();
          enqueueSnackbar("Account activation is taking longer than expected. Please try again later.", {
            variant: "warning",
          });
        }

        return status;
      } catch (error) {
        Sentry.captureException(error);
        if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
          stopPollingStatus();
          enqueueSnackbar("Failed to check account status. Please refresh and try again.", {
            variant: "error",
          });
        }
        return null;
      }
    };

    // Check immediately
    const initialResult = await checkStatus();
    if (initialResult?.status !== HyperliquidConnectionStatus.WAITING_DEPOSIT) {
      return initialResult;
    }

    // Set up interval for subsequent checks
    pollingIntervalRef.current = setInterval(checkStatus, POLLING_INTERVAL_MS);

    return initialResult;
  }, [agentData, agentDataQueryKey, apiHelper, auth, hubUser?.id, queryClient, stopPollingStatus]);

  // Transfer USD to spot
  const { mutateAsync: transferUsd, isPending: isTransferring } = useMutation({
    mutationFn: async ({ amount, toPerp }: { amount: string; toPerp: boolean }) => {
      const nonce = Date.now();

      const { signature, action } = await signTransfer({
        hyperliquidChain: HYPERLIQUID_CHAIN_MAINNET,
        signatureChainId: HYPERLIQUID_DEFAULT_CHAIN_ID_HEX,
        amount,
        toPerp,
        nonce,
      });

      const payload = {
        action,
        signature,
        nonce,
      };

      const result = await postHyperliquidExchange<{ status: "ok" | "err"; response?: unknown }>(payload);

      if (result.status === "err") {
        const errorMessage =
          typeof result.response === "string" && result.response.length > 0 ? result.response : "Transfer failed";
        throw new Error(errorMessage);
      }

      return { signature, action, result };
    },
    onSuccess: () => {
      // Update the cached agent data with the new status
      if (agentData && hubUser?.id) {
        queryClient.setQueryData(agentDataQueryKey, {
          ...agentData,
          status: HyperliquidConnectionStatus.ONLINE,
        });
      }
      enqueueSnackbar("Transfer successful! Funds moved to Spot account.", { variant: "success" });
    },
    onError: (error) => {
      if (isWalletActionError(error) && error.code === WALLET_ACTION_ERROR_CODE.USER_REJECTED) {
        return;
      }
      enqueueSnackbar(`Transfer failed: ${error.message}`, { variant: "error" });
    },
  });

  return {
    isWalletConnected,
    walletStatus,
    connectedAddress,

    // Eligibility check - use effectiveEligibility which considers existing accounts as eligible
    eligibilityData: effectiveEligibility,
    // Include isPendingAgentData to cover the brief moment when query is enabled but hasn't started fetching yet
    isCheckingEligibility: computedIsCheckingEligibility,
    eligibilityError: isNotEligibleError ? agentError : eligibilityError,
    isEligible: effectiveEligibility?.eligible ?? false,
    eligibilityReason: effectiveEligibility?.reason,
    isNotEligibleError,

    agentData: agentData ?? DEFAULT_AGENT_DATA,
    hasAgentData: !!agentData,
    agentError: agentError ?? null,
    isLoadingAgent,
    refetchAgentData,

    approveAgent: () => approveAgent(),
    isApproving,
    approvalError,
    resetApprovalError,

    updateHyperliquidAgentStatus,
    isUpdatingAgentStatus,

    // Polling for status after deposit
    startPollingStatus,
    stopPollingStatus,
    isPollingStatus,

    transferUsd,
    isTransferring,

    isLoading:
      computedIsCheckingEligibility || isApproving || isTransferring || isUpdatingAgentStatus || isPollingStatus,
  };
}
